import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, CheckCircle2, X, IdCard, MapPin, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { dptApi } from '../lib/api';
import { KECAMATAN_LIST } from '../mock/mockData';

const DptSearchBox = ({ onPick, picked, onClear, defaultKecamatan = '' }) => {
  const [query, setQuery] = useState('');
  const [selectedKecamatan, setSelectedKecamatan] = useState(defaultKecamatan || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (defaultKecamatan) {
      setSelectedKecamatan(defaultKecamatan);
    }
  }, [defaultKecamatan]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      dptApi.search(q, { kecamatan: selectedKecamatan || undefined, limit: 50 })
        .then(res => {
          setResults(Array.isArray(res.data) ? res.data : []);
          setOpen(true);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, selectedKecamatan]);

  if (picked) {
    return (
      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <p className="text-sm font-bold text-emerald-800 truncate">
            Data diambil dari DPT: <span className="underline">{picked.nama}</span> · {picked.kecamatan} · {picked.desa || '-'} · TPS {picked.tps ? String(picked.tps).replace(/^tps\s*/i, '') : '-'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 flex-shrink-0 bg-white px-2.5 py-1 rounded-lg border border-emerald-300"
        >
          <X className="w-3.5 h-3.5" /> Ganti / Cari Ulang
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4 relative" ref={containerRef}>
      <div className="p-3.5 rounded-xl bg-orange-50/60 border border-orange-200/80 shadow-sm">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <p className="text-xs font-bold text-orange-900 flex items-center gap-1.5">
            <IdCard className="w-4 h-4 text-orange-600" />
            Cari dari Data DPT (Hampir 2 Juta Pemilih) — Otomatis Mengisi Form
          </p>
          <div className="flex items-center gap-1.5 text-xs">
            <MapPin className="w-3.5 h-3.5 text-gray-500" />
            <select
              value={selectedKecamatan}
              onChange={(e) => setSelectedKecamatan(e.target.value)}
              className="h-8 text-xs font-semibold rounded-lg border border-orange-200 bg-white px-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer"
            >
              <option value="">Semua Kecamatan (Kabupaten)</option>
              {KECAMATAN_LIST.map((k) => (
                <option key={k.name} value={k.name}>
                  Kec. {k.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (results.length > 0) setOpen(true);
            }}
            placeholder={
              selectedKecamatan
                ? `Ketik nama pemilih di Kec. ${selectedKecamatan}... (mis. Hendi Wahyudin)`
                : 'Ketik nama pemilih... (mis. Hendi Wahyudin / Isep Rusdian)'
            }
            className="pl-9 pr-9 h-10 bg-white text-xs sm:text-sm border-orange-200 focus-visible:ring-orange-500"
          />
          {searching ? (
            <Loader2 className="w-4 h-4 animate-spin text-orange-500 absolute right-3 top-1/2 -translate-y-1/2" />
          ) : query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults([]);
                setOpen(false);
              }}
              className="w-4 h-4 text-gray-400 hover:text-gray-600 absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>

        {open && results.length > 0 && (
          <div className="mt-2 border border-orange-200 rounded-xl bg-white max-h-60 overflow-y-auto shadow-xl z-50">
            <div className="px-3 py-1.5 bg-orange-50 border-b border-orange-100 flex items-center justify-between text-[11px] font-semibold text-orange-800">
              <span>Ditemukan {results.length} data pemilih di DPT</span>
              {results.length >= 50 && (
                <span className="text-[10px] text-gray-500">Maks. 50 ditampilkan</span>
              )}
            </div>
            {results.map((r) => (
              <button
                key={r.id || `${r.nama}-${r.kecamatan}-${r.desa}-${r.tps}`}
                type="button"
                onClick={() => {
                  onPick(r);
                  setOpen(false);
                  setQuery('');
                }}
                className="w-full text-left px-3.5 py-2.5 hover:bg-orange-50/80 border-b border-gray-100 last:border-0 transition-colors flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-extrabold text-gray-900 truncate">
                    {r.nama}
                  </p>
                  <p className="text-[11px] text-gray-500 font-medium mt-0.5 truncate">
                    <span className="font-semibold text-gray-700">Kec. {r.kecamatan}</span>
                    {r.desa && <span> · Desa {r.desa}</span>}
                    {(r.rw || r.rt) && (
                      <span> · RW {r.rw || '-'}/RT {r.rt || '-'}</span>
                    )}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-bold">
                    TPS {r.tps ? String(r.tps).replace(/^tps\s*/i, '') : '-'}
                  </span>
                </div>
              </button>
            ))}
            {results.length >= 50 && (
              <div className="px-3 py-2 bg-gray-50 text-[11px] text-gray-600 font-medium text-center border-t border-gray-100">
                Nama yang dicari belum terlihat? Pilih <b>Kecamatan</b> di atas atau ketik nama lebih lengkap.
              </div>
            )}
          </div>
        )}

        {open && !searching && query.trim().length >= 2 && results.length === 0 && (
          <div className="mt-2 p-3 rounded-lg bg-white border border-gray-200 text-xs text-gray-600 flex items-start gap-2 shadow-sm">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-800">
                Tidak ditemukan pemilih dengan nama &quot;{query}&quot;
                {selectedKecamatan ? ` di Kec. ${selectedKecamatan}` : ' di DPT'}.
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {selectedKecamatan
                  ? 'Coba ganti pilihan Kecamatan ke "Semua Kecamatan", atau cek ejaan nama.'
                  : 'Pastikan ejaan nama sesuai data resmi, atau Anda tetap bisa mengisi form di bawah secara manual.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DptSearchBox;
